/**
 * 継ぎ目の表示規則（FR-021）。
 *
 * | 段階 | 結合した辺 | 未結合の辺 |
 * |---|---|---|
 * | 第 1 段階（秘文字） | 描画したまま（秘文字より奥 = `under`） | 描画（秘文字より手前 = `normal`） |
 * | 第 2 段階（画像） | 消える（`joined`） | 描画（`normal`） |
 *
 * 第 1 段階で辺を消さないのは、画像そのものがまだシャッフルされていて辺を消す意味がないため
 * である。結合の手がかりは秘文字の連続性が担う（`04-architecture.md` 継ぎ目の描画規則）。
 * 未結合の辺でベベルを秘文字より手前に描くのは、ピースが 1 枚ずつの札に見えるようにする
 * ためで、結合した辺だけが奥へ回り、秘文字が辺をまたいで連続する（TK-040）。
 *
 * 判断ではなく状態の写像であるため純関数として切り出してある。DOM を持たないので、
 * 規則そのものをユニットテストで検証できる。
 */

import type { Groups } from '../core/groups';
import type { CellIndex, Placement } from '../core/types';
import { at } from './internal';
import type { Edge } from './piece-view';
import { neighborCell } from './piece-view';

/** 段階。第 1 段階は秘文字、第 2 段階は画像。 */
export type Phase = 1 | 2;

/**
 * セル `cell` のピースの `edge` 側の継ぎ目が消えるか。
 *
 * 消えるのは第 2 段階で、その辺の向こうに**同じグループのピースがいる**ときだけである。
 *
 * 盤面上で隣接する 2 ピースが同じグループに属するなら、両者は必ず結合している。
 * グループ内では相対位置が目標配置の相対位置と一致するため（不変条件 I-2）、盤面上で
 * 隣り合っていること自体が結合条件そのものになるからである。したがって「同じグループか」
 * を見るだけでよく、結合条件を再計算する必要はない。
 */
export function seamJoined(
  cell: CellIndex,
  edge: Edge,
  placement: Placement,
  groups: Groups,
  C: number,
  R: number,
  phase: Phase,
): boolean {
  return seamState(cell, edge, placement, groups, C, R, phase) === 'joined';
}

/** 継ぎ目の表示状態（`view/piece-view.setSeamState` の入力）。 */
export type SeamState = 'normal' | 'under' | 'joined';

/**
 * セル `cell` のピースの `edge` 側の継ぎ目の表示状態（TK-040）。
 *
 * 隣接する同一グループの辺は、第 2 段階では消え（`joined`）、第 1 段階では秘文字より
 * 奥へ回る（`under`。秘文字が辺をまたいで連続する）。それ以外は秘文字より手前（`normal`）。
 */
export function seamState(
  cell: CellIndex,
  edge: Edge,
  placement: Placement,
  groups: Groups,
  C: number,
  R: number,
  phase: Phase,
): SeamState {
  const neighbor = neighborCell(cell, edge, C, R);
  // 盤外に面した辺は常に描く。隣が居ない以上「結合して 1 枚になった」ことはありえない。
  if (neighbor === null) return 'normal';
  const same =
    at(groups.labelOf, at(placement, neighbor)) === at(groups.labelOf, at(placement, cell));
  if (!same) return 'normal';
  return phase === 2 ? 'joined' : 'under';
}
